import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { TrendingUp, FileText, Tag, ArrowRight, Plus } from "lucide-react";
import KeywordTable from "./KeywordTable";

interface Props {
  onOpenArticle: (id: string) => void;
  onUseKeyword: (kw: string) => void;
  onGotoCompetitor: () => void;
  onGotoResearch: () => void;
}

interface BlogArticleRow { id: string; title: string; slug: string; status: string; updated_at: string; published_at: string | null; }
interface KeywordRow { id: string; keyword: string; volume_estimate?: number | null; difficulty?: number | null; intent?: string | null; updated_at?: string | null; created_at?: string | null; source: "favorite" | "queue"; }

const fmtDate = (iso?: string | null) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "short" }) : "";

const GuideDashboard = ({ onOpenArticle, onUseKeyword, onGotoCompetitor, onGotoResearch }: Props) => {
  const [recent, setRecent] = useState<BlogArticleRow[] | null>(null);
  const [keywords, setKeywords] = useState<KeywordRow[] | null>(null);
  const [snapshot, setSnapshot] = useState<{ fetched_at: string; payload: any } | null>(null);

  useEffect(() => {
    (async () => {
      const [{ data: arts }, { data: favs }, { data: queue }, { data: snap }] = await Promise.all([
        (supabase as any).from("blog_articles").select("id,title,slug,status,updated_at,published_at").order("updated_at", { ascending: false }).limit(5),
        (supabase as any).from("blog_keyword_favorites").select("id,keyword,volume_estimate,difficulty,intent,updated_at,created_at").order("updated_at", { ascending: false }).limit(8),
        (supabase as any).from("blog_keyword_queue").select("id,keyword,created_at").order("created_at", { ascending: false }).limit(8),
        (supabase as any).from("seo_competitor_snapshots").select("fetched_at,payload").eq("domain", "maroctransfers.com").order("fetched_at", { ascending: false }).limit(1).maybeSingle(),
      ]);
      setRecent(arts || []);
      const merged: KeywordRow[] = [
        ...((favs || []).map((f: any) => ({ ...f, source: "favorite" as const }))),
        ...((queue || []).map((q: any) => ({ id: q.id, keyword: q.keyword, source: "queue" as const, created_at: q.created_at }))),
      ];
      // De-dup by keyword (favorite wins)
      const seen = new Set<string>();
      const deduped = merged.filter((k) => {
        const key = k.keyword.toLowerCase();
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      }).slice(0, 12);
      setKeywords(deduped);
      setSnapshot(snap || null);
    })();
  }, []);

  return (
    <div className="space-y-4">
      {/* Tableau central des mots-clés du Guide */}
      <KeywordTable onGenerated={onOpenArticle} />

      <div className="grid gap-4 lg:grid-cols-3">
      {/* Bloc 1 — Concurrence */}
      <Card className="lg:col-span-1">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-primary" />
            Concurrence
          </CardTitle>
          <p className="text-xs text-muted-foreground">maroctransfers.com — opportunités SEO</p>
        </CardHeader>
        <CardContent className="space-y-3">
          {snapshot?.payload?.gap_keywords?.length ? (
            <div className="space-y-1.5">
              <p className="text-xs font-medium text-muted-foreground">Top opportunités</p>
              {(snapshot.payload.gap_keywords as any[]).slice(0, 5).map((k: any, i: number) => (
                <div key={i} className="flex items-center justify-between text-sm rounded-md bg-muted/40 px-2 py-1.5">
                  <span className="truncate">{k.keyword || k}</span>
                  {k.volume ? <Badge variant="secondary" className="text-xs">{k.volume}</Badge> : null}
                </div>
              ))}
              <p className="text-[11px] text-muted-foreground">Mis à jour : {new Date(snapshot.fetched_at).toLocaleString("fr-FR")}</p>
            </div>
          ) : (
            <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
              Aucun instantané concurrent en cache.<br />
              Lancez une analyse depuis l'onglet « Concurrent ».
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full" onClick={onGotoCompetitor}>
            Analyser la concurrence <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </CardContent>
      </Card>

      {/* Bloc 2 — Derniers articles */}
      <Card className="lg:col-span-1">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <FileText className="h-4 w-4 text-primary" />
            Derniers articles
          </CardTitle>
          <p className="text-xs text-muted-foreground">Cliquez pour éditer instantanément</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {recent === null ? (
            <>{[1,2,3].map((i) => <Skeleton key={i} className="h-12 w-full" />)}</>
          ) : recent.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Aucun article pour le moment.</p>
          ) : recent.map((a) => (
            <button
              key={a.id}
              onClick={() => onOpenArticle(a.id)}
              className="w-full text-left rounded-md border border-border p-2 hover:bg-accent transition flex items-center justify-between gap-2"
            >
              <div className="min-w-0 flex-1">
                <div className="text-sm font-medium truncate">{a.title || a.slug}</div>
                <div className="text-[11px] text-muted-foreground truncate">
                  {a.status === "published" && a.published_at ? `📅 ${fmtDate(a.published_at)} · ` : ""}
                  ✏️ {fmtDate(a.updated_at)}
                </div>
              </div>
              <Badge variant={a.status === "published" ? "default" : "secondary"} className="text-[10px] shrink-0">
                {a.status === "published" ? "Publié" : "Brouillon"}
              </Badge>
            </button>
          ))}
        </CardContent>
      </Card>

      {/* Bloc 3 — Mots-clés Google */}
      <Card className="lg:col-span-1">
        <CardHeader className="pb-3">
          <CardTitle className="font-heading text-base flex items-center gap-2">
            <Tag className="h-4 w-4 text-primary" />
            Mots-clés à exploiter
          </CardTitle>
          <p className="text-xs text-muted-foreground">Favoris + file d'attente Google</p>
        </CardHeader>
        <CardContent className="space-y-2">
          {keywords === null ? (
            <>{[1,2,3].map((i) => <Skeleton key={i} className="h-9 w-full" />)}</>
          ) : keywords.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-3 text-xs text-muted-foreground">
              Aucun mot-clé enregistré.<br />
              Lancez la recherche pour en ajouter.
            </div>
          ) : (
            <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
              {keywords.map((k) => (
                <div key={`${k.source}-${k.id}`} className="flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1.5">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{k.keyword}</div>
                    <div className="text-[10px] text-muted-foreground flex flex-wrap gap-x-2">
                      {k.volume_estimate ? <span>vol. {k.volume_estimate}</span> : null}
                      {typeof k.difficulty === "number" ? <span>KD {k.difficulty}</span> : null}
                      {k.intent ? <span>{k.intent}</span> : null}
                      <Badge variant="outline" className="text-[9px] px-1 py-0">{k.source === "favorite" ? "Favori" : "Queue"}</Badge>
                    </div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => onUseKeyword(k.keyword)} title="Ajouter à la rédaction">
                    <Plus className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          <Button variant="outline" size="sm" className="w-full" onClick={onGotoResearch}>
            Rechercher de nouveaux mots-clés <ArrowRight className="ml-2 h-3.5 w-3.5" />
          </Button>
        </CardContent>
      </Card>
      </div>
    </div>
  );
};

export default GuideDashboard;